import React, { useMemo } from "react";
import { Mail, Phone, Globe, MapPin, Linkedin, Twitter, Instagram, Facebook, Youtube, Building2, Printer } from "lucide-react";
import { fileUrl } from "@/lib/api";

const socialIcons = { linkedin: Linkedin, twitter: Twitter, instagram: Instagram, facebook: Facebook, youtube: Youtube };

export default function CardPreview({ card, onSaveContact, onLead }) {
  const c = card || {};
  const gold = c.brand_color || "#D4AF37";
  const initials = (c.full_name || "iW").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const visibleProducts = useMemo(() => (c.products || []).filter((p) => p.title), [c.products]);

  return (
    <div className="w-full max-w-sm mx-auto rounded-[2rem] overflow-hidden shadow-2xl border border-[#ffffff1a]" style={{ background: "#0B1D3A" }} data-testid="card-preview">
      {/* Logo cover banner — spans the top; sits BEHIND the headshot */}
      <div className="h-28 relative overflow-hidden" data-testid="preview-logo-banner"
        style={{ background: `linear-gradient(120deg, ${gold}22, #0A1830 85%)` }}>
        <div className="absolute top-0 left-0 right-0 h-1.5 z-20" style={{ background: gold }} data-testid="brand-accent-bar" />
        {c.logo_url
          ? <img src={fileUrl(c.logo_url)} alt="logo" className={`absolute inset-0 h-full w-full ${c.logo_fit === "contain" ? "object-contain p-3" : "object-cover"}`} data-testid="preview-logo" />
          : <div className="absolute inset-0 flex items-center px-6"><span className="font-display text-xl font-bold text-[#F6F1E4]/70 truncate">{c.company || "Your Brand"}</span></div>}
      </div>
      <div className="px-6 pb-6">
        <div className="h-24 w-24 -mt-8 relative z-30 rounded-2xl border-4 overflow-hidden bg-[#142B5A] flex items-center justify-center shadow-lg" style={{ borderColor: gold }} data-testid="preview-avatar">
          {c.avatar_url ? <img src={fileUrl(c.avatar_url)} alt={c.full_name} className="h-full w-full object-cover" />
            : <span className="font-display text-2xl font-bold" style={{ color: gold }}>{initials}</span>}
        </div>
        <h2 className="font-display text-2xl font-bold text-[#F6F1E4] mt-4">{c.full_name || "Your Name"}</h2>
        {c.credentials && <p className="text-xs font-semibold text-[#8B9BB4] tracking-wide mt-0.5" data-testid="preview-credentials">{c.credentials}</p>}
        <p className="text-sm font-semibold" style={{ color: gold }}>{c.job_title || "Job Title"}</p>
        {c.tagline && <p className="text-sm italic text-[#c9d3e0] mt-1" data-testid="preview-tagline">“{c.tagline}”</p>}
        {c.company && <p className="text-sm text-[#8B9BB4] flex items-center gap-1.5 mt-1"><Building2 size={14} /> {c.company}</p>}
        {c.bio && <p className="text-sm text-[#8B9BB4] leading-relaxed mt-3">{c.bio}</p>}

        <div className="mt-5 space-y-2">
          {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-3 text-sm text-[#F6F1E4] hover:text-[#D4AF37] transition-colors"><Phone size={16} style={{ color: gold }} /> {c.phone}</a>}
          {c.fax && <p className="flex items-center gap-3 text-sm text-[#8B9BB4]" data-testid="preview-fax"><Printer size={16} style={{ color: gold }} /> Fax: {c.fax}</p>}
          {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-3 text-sm text-[#F6F1E4] hover:text-[#D4AF37] transition-colors break-all"><Mail size={16} style={{ color: gold }} /> {c.email}</a>}
          {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-sm text-[#F6F1E4] hover:text-[#D4AF37] transition-colors break-all"><Globe size={16} style={{ color: gold }} /> {c.website}</a>}
          {c.location && <p className="flex items-center gap-3 text-sm text-[#8B9BB4]"><MapPin size={16} style={{ color: gold }} /> {c.location}</p>}
        </div>

        {c.socials && Object.entries(c.socials).some(([, v]) => v) && (
          <div className="flex gap-2 mt-5">
            {Object.entries(c.socials).map(([k, v]) => {
              const Icon = socialIcons[k];
              if (!v || !Icon) return null;
              return <a key={k} href={v} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-lg flex items-center justify-center border border-[#ffffff1a] hover:border-[#D4AF37] transition-colors" style={{ color: gold }}><Icon size={16} /></a>;
            })}
          </div>
        )}

        {visibleProducts.length > 0 && (
          <div className="mt-6">
            <p className="overline mb-2">Products & Services</p>
            <div className="space-y-2">
              {visibleProducts.map((p, i) => (
                <div key={p._key || `${p.title}-${i}`} className="rounded-lg border border-[#ffffff1a] p-3">
                  <p className="text-sm font-semibold text-[#F6F1E4]">{p.title}</p>
                  {p.description && <p className="text-xs text-[#8B9BB4] mt-0.5">{p.description}</p>}
                  {p.cta_url && <a href={p.cta_url} target="_blank" rel="noreferrer" className="text-xs font-semibold mt-1 inline-block" style={{ color: gold }}>{p.cta_label || "Learn more"} →</a>}
                </div>
              ))}
            </div>
          </div>
        )}

        {c.gallery?.length > 0 && (
          <div className="mt-6">
            <p className="overline mb-2">Portfolio</p>
            <div className="grid grid-cols-3 gap-2">
              {c.gallery.map((g, i) => <img key={g} src={fileUrl(g)} alt="" className="aspect-square object-cover rounded-lg" />)}
            </div>
          </div>
        )}

        {(onSaveContact || onLead) && (
          <div className="mt-6 space-y-2">
            {onSaveContact && <button onClick={onSaveContact} data-testid="save-contact-btn" className="btn-gold w-full py-3 rounded-full text-sm">Save Contact</button>}
            {onLead && <button onClick={onLead} data-testid="exchange-contact-btn" className="btn-ghost w-full py-3 rounded-full text-sm">Exchange Details</button>}
          </div>
        )}
      </div>
    </div>
  );
}
